<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling-ai.UI.5 / SUMMARY.2, .4 — the AI call summary Studio Chat
// wrote back: the summary, an outcome chip, sentiment and the follow-up draft
// (with Copy). `compact` is the bubble's version (summary + outcome only);
// the call detail shows everything plus "Regenerate summary".
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  AI_ACCENT,
  AI_ICON,
  PAPER_CHIP,
  PAPER_EYEBROW,
} from './kaptionCallPaper';

const props = defineProps({
  summary: { type: Object, default: null },
  compact: { type: Boolean, default: false },
  canRegenerate: { type: Boolean, default: false },
  regenerating: { type: Boolean, default: false },
});
const emit = defineEmits(['regenerate']);
const { t } = useI18n();

const OUTCOME_TONES = {
  resolved: 'bg-n-teal-3 text-n-teal-11',
  sale: 'bg-n-teal-3 text-n-teal-11',
  follow_up: 'bg-n-amber-3 text-n-amber-11',
  unresolved: 'bg-n-ruby-3 text-n-ruby-11',
  complaint: 'bg-n-ruby-3 text-n-ruby-11',
};
const outcomeTone = computed(
  () => OUTCOME_TONES[props.summary?.outcome] || 'bg-n-alpha-2 text-n-slate-11'
);
const copied = ref(false);
const copyDraft = async () => {
  await copyTextToClipboard(props.summary.followUpDraft);
  copied.value = true;
  useAlert(t('KAPTION_CALLS.AI.COPIED'));
};
</script>

<template>
  <section
    v-if="summary || canRegenerate"
    data-testid="kaption-call-ai-summary"
    class="flex flex-col gap-2"
    :class="compact ? '' : 'rounded-lg border border-n-weak bg-n-alpha-1 p-4'"
  >
    <div class="flex items-center gap-2">
      <span class="size-3.5" :class="[AI_ICON, AI_ACCENT]" aria-hidden="true" />
      <span :class="PAPER_EYEBROW">{{
        $t('KAPTION_CALLS.AI.SUMMARY_TITLE')
      }}</span>
      <span
        v-if="summary?.outcome"
        data-testid="kaption-call-ai-outcome"
        class="ml-auto inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium leading-none"
        :class="outcomeTone"
      >
        {{ $t(`KAPTION_CALLS.AI.OUTCOME.${summary.outcome.toUpperCase()}`) }}
      </span>
    </div>
    <p
      v-if="summary"
      data-testid="kaption-call-ai-summary-text"
      class="mb-0 text-sm text-n-slate-12"
      :class="compact ? 'line-clamp-3 text-xs' : ''"
    >
      {{ summary.summary }}
    </p>
    <p v-else class="mb-0 text-sm text-n-slate-11">
      {{ $t('KAPTION_CALLS.AI.NO_SUMMARY') }}
    </p>
    <template v-if="summary && !compact">
      <div class="flex flex-wrap items-center gap-1.5">
        <span v-if="summary.intent" :class="PAPER_CHIP">{{
          summary.intent
        }}</span>
        <span
          v-if="summary.sentiment"
          data-testid="kaption-call-ai-sentiment"
          :class="PAPER_CHIP"
        >
          {{
            $t(`KAPTION_CALLS.AI.SENTIMENT.${summary.sentiment.toUpperCase()}`)
          }}
        </span>
      </div>
      <div
        v-if="summary.followUpDraft"
        data-testid="kaption-call-ai-follow-up"
        class="flex flex-col gap-1.5 rounded-md border border-n-weak bg-n-solid-1 p-3"
      >
        <span :class="PAPER_EYEBROW">{{
          $t('KAPTION_CALLS.AI.FOLLOW_UP')
        }}</span>
        <p class="mb-0 whitespace-pre-line text-sm text-n-slate-12">
          {{ summary.followUpDraft }}
        </p>
        <Button
          data-testid="kaption-call-ai-copy"
          icon="i-lucide-copy"
          :label="
            copied ? $t('KAPTION_CALLS.AI.COPIED') : $t('KAPTION_CALLS.AI.COPY')
          "
          variant="link"
          color="slate"
          size="xs"
          class="self-start"
          @click="copyDraft"
        />
      </div>
    </template>
    <Button
      v-if="canRegenerate && !compact"
      data-testid="kaption-call-ai-regenerate"
      icon="i-lucide-refresh-cw"
      :label="$t('KAPTION_CALLS.AI.REGENERATE')"
      variant="ghost"
      color="slate"
      size="sm"
      class="self-start"
      :is-loading="regenerating"
      @click="emit('regenerate')"
    />
  </section>
</template>
